<template>
    <transition
        enter-active-class="transition ease-out duration-700 transform"
        enter-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75 transform"
        leave-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
    >
        <div v-show="isPreviewOn" class="mt-8 bg-white overflow-hidden shadow rounded-lg">
            <div class="border-b border-gray-200 px-4 py-5 sm:px-6">
                <div class="lg:flex lg:items-center lg:justify-between">
                    <div class="flex-1 min-w-0">
                        <h3 class="text-lg leading-6 font-medium text-gray-900">
                            {{ trans('Posting preview') }}
                        </h3>
                    </div>
                    <div class="mt-5 flex lg:mt-0 lg:ml-4">
                        <span class="block shadow-sm rounded-md">
                          <button @click="$emit('preview-closed')" type="button"
                                  class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:ring-blue focus:border-blue-300 active:text-gray-800 active:bg-gray-50 transition duration-150 ease-in-out">
                            <svg class="-ml-1 mr-2 h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                              <path
                                  d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/>
                            </svg>
                            {{ trans('Edit') }}
                          </button>
                        </span>


                        <span class="sm:ml-3 shadow-sm rounded-md">
                            <button @click="$emit('publish')" type="button"
                                    class="inline-flex items-center px-4 py-2 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-500 focus:outline-none focus:ring-indigo focus:border-indigo-700 active:bg-indigo-700 transition duration-150 ease-in-out">
                                <svg class="-ml-1 mr-2 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd"
                                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                          clip-rule="evenodd"/>
                                </svg>
                            {{ trans('Publish') }}
                            </button>
                        </span>
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-1 gap-y-8 gap-x-6 sm:grid-cols-6">
                <div class="sm:col-span-4">
                    <div class="px-4 py-5 sm:p-6 ">
                        <h2 class="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:leading-9 sm:truncate">
                            {{ data.title }}
                        </h2>
                        <div class="mt-1 flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap">
                            <div class="mt-2 flex items-center text-sm leading-5 text-gray-500 sm:mr-6">
                                <svg class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" viewBox="0 0 20 20"
                                     fill="currentColor">
                                    <path fill-rule="evenodd"
                                          d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                                          clip-rule="evenodd"/>
                                    <path
                                        d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z"/>
                                </svg>
                                {{ getTypeName(data.type, $page.board.job_types) }}
                            </div>
                            <div class="mt-2 flex items-center text-sm leading-5 text-gray-500 sm:mr-6">
                                <svg class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" viewBox="0 0 20 20"
                                     fill="currentColor">
                                    <path fill-rule="evenodd"
                                          d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                          clip-rule="evenodd"/>
                                </svg>
                                {{ data.location_name }}
                            </div>
                            <div class="mt-2 flex items-center text-sm leading-5 text-gray-500">
                                <svg class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" viewBox="0 0 20 20"
                                     fill="currentColor">
                                    <path fill-rule="evenodd"
                                          d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                                          clip-rule="evenodd"/>
                                </svg>
                                {{ data.created_at }}
                            </div>
                        </div>
                    </div>
                    <div class="px-4 py-5 sm:p-6 prose lg:prose-lg" v-html="data.description"></div>
                </div>
                <div class="sm:col-span-2">
                    <div class="bg-gray-50 overflow-hidden rounded-lg m-4">
                        <div class="px-4 py-5 sm:p-6">
                            <div class="flex flex-col items-center">
                                <img class="inline-block h-32 w-32 rounded-full"
                                     :class="data.company_uploaded_logo_src || data.company_existing_logo_absolute ? '': 'bg-pink-500'"
                                     :src="data.company_uploaded_logo_src || data.company_existing_logo_absolute" alt="">
                                <div class="mt-4 text-xl font-bold leading-7 text-gray-900">{{ data.company_name }}</div>
                                <div class="mt-4 flex items-center text-sm leading-5 text-gray-500">
                                    <svg class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" viewBox="0 0 20 20"
                                         fill="currentColor">
                                        <path fill-rule="evenodd"
                                              d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                              clip-rule="evenodd"/>
                                    </svg>
                                    {{ data.location_name }}
                                </div>
                                <div class="mt-4">
                                    <a class="text-xs uppercase hover:underline" href="#" target="_blank">{{ trans('Visit company\'s website') }}</a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>


            <div class="border-t border-gray-200 px-4 py-4 sm:px-6">
                <span class="inline-flex rounded-md shadow-sm">
                    <button type="button" class="inline-flex items-center px-4 py-2 border border-transparent
                    text-base leading-6 font-medium rounded-md text-white
                    transition ease-in-out duration-150" :class="theme.primary_button">
                    {{ trans('Apply for this position') }}
                    </button>
                </span>
            </div>
        </div>
    </transition>
</template>

<script>
    export default {
        name: "PostingPreview",
        props: {
            isPreviewOn: Boolean,
            data: Object,
            lang: Object|String,
            theme: Object
        },
        methods: {
            trans(value) {
                if (Object.keys(this.lang).length === 0) {
                    return value;
                }

                return this.lang[value] || value;
            },
            getTypeName(id, types) {
                for (var i=0; i < types.length; i++) {
                    if (types[i].id === parseInt(id, 10)) {
                        return types[i].name;
                    }
                }
            }
        }
    }
</script>
